import * as React from 'react'

import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { CardDetailFragment } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { canActivateCard } from 'modules/banking/lib/cards/canActivateCard'
import { isPending } from 'modules/banking/lib/cards/isPending'
import { isVirtual } from 'modules/banking/lib/cards/isVirtual'

const carrierHumanName = (carrierCode: string) => {
  switch (carrierCode) {
    case 'fedex':
      return 'Fedex'
    case 'usps':
      return 'USPS'
    case 'royal_mail':
      return 'Royal Mail'
    case 'dhl':
      return 'DHL'
    default:
      return carrierCode
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
  }
}

const shippedCopy = (card: CardDetailFragment) => {
  const labelOptions = {
    shipping_date: (
      <Text.Normal weight="medium" color="grey1000">
        {moment(card.shipping?.sentAt).format('dddd Do MMM YYYY')}
      </Text.Normal>
    ),

    carrier: (
      <Text.Normal weight="medium" color="grey1000">
        {carrierHumanName(card.shipping?.carrier || '')}
      </Text.Normal>
    ),
  }

  if (card.shipping?.status === 'delivered') {
    return i18n.tView('expenses_v2.cards.details.pending_activation_warning', labelOptions)
  } else if (card.shipping?.sentAt && card.shipping?.carrier && card.shipping?.address) {
    return i18n.tView('expenses_v2.cards.shipping.shipped', labelOptions)
  } else {
    return i18n.tView('expenses_v2.cards.shipping.shipped_without_all_info', labelOptions)
  }
}

export const CardTrackInfo: React.FC<{ card: CardDetailFragment; canManageCards: boolean }> = ({
  card,
  canManageCards,
}) => {
  const canSeeTracking = canActivateCard(card) || canManageCards
  if (!isPending(card) || isVirtual(card) || !canSeeTracking) {
    return null
  }

  const pendingCardStatusMessage = () => {
    switch (card.shipping?.status) {
      case 'pending':
        return i18n.t('expenses_v2.cards.shipping.pending')
      case null:
        return i18n.t('expenses_v2.cards.details.pending_activation_warning')
      default:
        return (
          <Box>
            <Text.Normal color="grey1000">{shippedCopy(card)}</Text.Normal>
            {card.shipping?.address && (
              <Box paddingY="s16">
                <Text.Normal color="grey1000" htmlTag="p">
                  {card.shipping?.address.fullName}
                </Text.Normal>

                {card.shipping.address?.phoneNumber && (
                  <Text.Normal color="grey1000" htmlTag="p">
                    {card.shipping?.address.phoneNumber}
                  </Text.Normal>
                )}

                <Text.Normal color="grey1000" htmlTag="p">
                  {card.shipping?.address.line1}
                </Text.Normal>

                {card.shipping?.address.line2 && (
                  <Text.Normal htmlTag="p">
                    <Text.Normal>{card.shipping?.address.line2}</Text.Normal>
                  </Text.Normal>
                )}

                <Text.Normal color="grey1000" htmlTag="p">
                  {card.shipping?.address.postalCode}&nbsp;
                  {card.shipping?.address.city}&nbsp;
                  {card.shipping?.address.state}&nbsp;
                  {card.shipping?.address.country}
                </Text.Normal>
              </Box>
            )}

            {card.shipping?.trackingUrl && (
              <a
                href={card.shipping?.trackingUrl}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <Box gap="s8" flexDirection="row" alignItems="center" inline>
                  <Text.Normal color="grey1000" size="300" weight="medium">
                    {i18n.t('expenses_v2.cards.shipping.track_shipment_cta')}
                  </Text.Normal>
                  <Icon.Medium icon="arrow-right" color="grey1000" />
                </Box>
              </a>
            )}
          </Box>
        )
    }
  }

  return (
    <Box>
      <Alert color="warning">{pendingCardStatusMessage()}</Alert>
    </Box>
  )
}
